"use client";

import { useState } from "react";

import { Button } from "~/components/ui/button";
import { copyTextToClipboard } from "~/hooks/useCopyToClipboard";

export interface CommandOutputBlockProps {
  readonly command: string;
  /** Small prefix before the command, e.g. the step number in an archive hook. */
  readonly prefix?: string;
  readonly statusLabel: string;
  readonly durationMs: number;
  readonly stdout: string;
  readonly stderr: string;
  readonly stdoutTruncated: boolean;
  readonly stderrTruncated: boolean;
  readonly defaultExpanded?: boolean;
}

/**
 * One command's captured output, collapsible and copyable.
 *
 * Shared by the archive hook modal and the background project-action modal:
 * both render exactly one command per block, and duplicating the copy/truncation
 * details in two places is how they drift apart.
 */
export function CommandOutputBlock({
  command,
  prefix,
  statusLabel,
  durationMs,
  stdout,
  stderr,
  stdoutTruncated,
  stderrTruncated,
  defaultExpanded = true,
}: CommandOutputBlockProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const copyOutput = () => {
    const text = [
      `$ ${command}`,
      stdout.length > 0 ? stdout : "(stdout vacío)",
      stderr.length > 0 ? `--- stderr ---\n${stderr}` : "",
    ]
      .filter((section) => section.length > 0)
      .join("\n\n");
    // `navigator.clipboard` is secure-context only; copyTextToClipboard falls
    // back to `document.execCommand` when the app is served over plain HTTP.
    void copyTextToClipboard(text).catch(() => undefined);
  };

  return (
    <div className="rounded-md border bg-muted/50 text-xs">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left font-mono"
      >
        <span className="truncate">
          {prefix ? <span className="text-muted-foreground">{prefix} </span> : null}
          {command}
        </span>
        <span className="shrink-0 text-muted-foreground">
          {statusLabel} · {durationMs}ms
        </span>
      </button>
      {expanded ? (
        <div className="flex flex-col gap-2 border-t px-3 py-2">
          {stdout.length > 0 ? (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-muted-foreground">
              {stdout}
              {stdoutTruncated ? "\n… (stdout truncated)" : ""}
            </pre>
          ) : null}
          {stderr.length > 0 ? (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-mono text-destructive">
              {stderr}
              {stderrTruncated ? "\n… (stderr truncated)" : ""}
            </pre>
          ) : null}
          <div className="flex flex-row-reverse">
            <Button type="button" size="sm" variant="ghost" onClick={copyOutput}>
              Copiar output
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
